import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

interface SectionHeadingProps {
  /** Small uppercase label above the heading (e.g. "What we do"). */
  eyebrow?: ReactNode;
  /** Main heading text in brand green. */
  children: ReactNode;
  /** Optional accent word (rendered in brand gold) appended after children. */
  accent?: ReactNode;
  /** Center the heading block (eyebrow + heading + bar). */
  centered?: boolean;
  /** Render light text — use inside dark-background sections. */
  onDark?: boolean;
  /** Hide the gold underline bar. */
  hideBar?: boolean;
  /** Heading level (defaults to h2). */
  as?: "h1" | "h2" | "h3";
  /** Extra classes for the heading element itself. */
  className?: string;
  /** Extra classes for the wrapper. */
  wrapperClassName?: string;
}

/**
 * Brand section heading — renders the eyebrow + green heading + gold
 * accent word + gold underline bar pattern. Pulls colors from the global
 * heading tokens (`--heading`, `--heading-accent`) so changing the brand
 * palette in `index.css` automatically updates every heading.
 */
export const SectionHeading = ({
  eyebrow,
  children,
  accent,
  centered = false,
  onDark = false,
  hideBar = false,
  as: Tag = "h2",
  className,
  wrapperClassName,
}: SectionHeadingProps) => {
  const sizeByLevel = {
    h1: "text-4xl sm:text-5xl lg:text-6xl",
    h2: "text-4xl lg:text-5xl",
    h3: "text-2xl lg:text-3xl",
  } as const;

  return (
    <div
      className={cn(
        centered && "text-center mx-auto",
        wrapperClassName,
      )}
    >
      {eyebrow && (
        <span className="heading-eyebrow block mb-2">{eyebrow}</span>
      )}
      <Tag
        className={cn(
          onDark ? "heading-brand-on-dark" : "heading-brand",
          sizeByLevel[Tag],
          "leading-tight",
          className,
        )}
      >
        {children}
        {accent && (
          <>
            {" "}
            <span className="brand-accent">{accent}</span>
          </>
        )}
      </Tag>
      {!hideBar && (
        <span
          className={cn("heading-bar", centered && "mx-auto")}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default SectionHeading;